import { createPortal } from 'react-dom'
import { useFullscreenHost } from '@/lib/fullscreen'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowRightLeft, Ban, Bell, BellOff, Check, ChevronRight, Copy, Flag, HeadphoneOff, Headphones, Lock, LogOut, MessageSquare, Mic, MicOff, MonitorSpeaker, PencilLine, PhoneOff, Shield, Timer, UserPlus, UserRound, Volume2 } from 'lucide-react'
import { ConfirmModal } from '@/components/guild/ConfirmModal'
import { NickModal } from '@/components/guild/NickModal'
import { ReportModal } from '@/components/ReportModal'
import { describeError } from '@/lib/auth'
import { cx } from '@/lib/cx'
import { guildApi } from '@/lib/guildApi'
import { isUnread } from '@/lib/guildUi'
import { DM_SETTINGS, ownMuted } from '@/lib/muting'
import { P, assignLockHint, basePermissions, channelPermissions, has, topRole } from '@/lib/permissions'
import { voiceChannelsInOrder } from '@/lib/channelOrder'
import { socialApi } from '@/lib/socialApi'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useContextMenuStore, type UserMenuTarget } from '@/store/contextMenu'
import { useGuildStore } from '@/store/guilds'
import { useVoiceStore } from '@/store/voice'
import { displayName, useSocialStore } from '@/store/social'
import { usePresenceStore, useCustomStatus, useStatusOf } from '@/store/presence'
import { activityOf } from '@/lib/presence'
import type { User } from '@/types/api'
import { ProfileCard } from './profile/ProfileCard'
import { ProfileModal, type ProfileTab } from './profile/ProfileModal'
import styles from './UserContextMenu.module.css'

// MODERATE_MEMBERS (тайм-аут) может отсутствовать в наборе прав — тогда считаем по KICK_MEMBERS.
const CARD_WIDTH = 360

const MODERATE_MEMBERS: bigint = (P as Record<string, bigint>).MODERATE_MEMBERS ?? P.KICK_MEMBERS
const MENU_WIDTH = 240

/** Контекстное меню по правому клику на пользователе: профиль, ЛС, друзья, жалоба и модерация на сервере. */
export function UserContextMenu() {
  const target = useContextMenuStore((s) => s.target)
  const host = useFullscreenHost()
  if (!target) return null
  const menu = <UserMenu key={`${target.userId}:${target.x}:${target.y}`} target={target} />
  // в полноэкранном режиме браузер показывает только развёрнутый элемент: меню, нарисованное
  // на уровне страницы, было бы не видно — поэтому рисуем его внутрь плитки
  return host ? createPortal(menu, host) : menu
}


function UserMenu({ target }: { target: UserMenuTarget }) {
  const close = useContextMenuStore((s) => s.close)
  const me = useAuthStore((s) => s.user)
  const setView = useAppStore((s) => s.setView)
  const devMode = useAppStore((s) => s.devMode)
  const sendMessage = useSocialStore((s) => s.sendMessage)
  const guild = useGuildStore((s) => (target.guildId ? s.guilds[target.guildId] : undefined))
  const member = useGuildStore((s) => (target.guildId ? s.members[target.guildId]?.[target.userId] : undefined))
  const self = useGuildStore((s) => (target.guildId && me ? s.members[target.guildId]?.[me.id] : undefined))
  const voice = useGuildStore((s) => (target.guildId ? s.voiceStates[target.guildId]?.[target.userId] : undefined))
  const channels = useGuildStore((s) => (target.guildId ? s.channels[target.guildId] : undefined))
  const setMemberRole = useGuildStore((s) => s.setMemberRole)
  const relationship = useSocialStore((s) => s.relationships[target.userId])
  const status = useStatusOf(target.userId)
  const activity = useSocialStore((s) => s.activities[target.userId])
  const customStatus = useCustomStatus(target.userId)
  const activities = usePresenceStore((s) => s.activities[target.userId])
  const guilds = useGuildStore((s) => s.guilds)
  const inRoom = useVoiceStore((s) => Boolean(s.remote[target.userId]))
  const volume = useVoiceStore((s) => s.prefs.userVolumes[target.userId] ?? 1)
  const setUserVolume = useVoiceStore((s) => s.setUserVolume)
  const streaming = useVoiceStore((s) => Boolean(s.screens[target.userId]))
  const streamVolume = useVoiceStore((s) => s.prefs.streamVolumes[target.userId] ?? 1)
  const setStreamVolume = useVoiceStore((s) => s.setStreamVolume)
  // меню на строке беседы: её «прочитано» и mute
  const dmChannel = useSocialStore((s) => (target.dmChannelId ? s.channels[target.dmChannelId] : undefined))
  const dmReadState = useSocialStore((s) => (target.dmChannelId ? s.readStates[target.dmChannelId] : undefined))
  const ack = useSocialStore((s) => s.ack)
  const dmMuted = useGuildStore((s) => (target.dmChannelId ? ownMuted(s.settings[DM_SETTINGS], target.dmChannelId) : false))
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const user: User = target.user ?? member?.user ?? relationship?.user ?? { id: target.userId, username: '', global_name: null, avatar: null }
  const known = Boolean(user.username)
  const name = member?.nick ?? (known ? displayName(user) : target.userId)
  const isMe = me?.id === target.userId

  const ref = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState({ left: target.x, top: target.y })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // «Профиль» из меню и переход по общим друзьям открывают большое окно сразу
  const [dialog, setDialog] = useState<'profile' | 'report' | 'kick' | 'ban' | null>(target.mode === 'profile' ? 'profile' : null)
  const [sub, setSub] = useState<'roles' | 'move' | null>(null)
  const [profileTab, setProfileTab] = useState<ProfileTab>(target.tab ?? 'activity')
  // окно «Изменить ник»: пока оно открыто, клик по нему не закрывает меню — окно живёт внутри него
  const [nickOpen, setNickOpen] = useState(false)
  // баннер, «о себе» и дата регистрации в списках не нужны — берём их, только когда карточку открыли
  const profile = useQuery({
    queryKey: ['profile', target.userId],
    queryFn: () => socialApi.profile(target.userId),
    enabled: dialog === 'profile' || target.mode === 'card',
    staleTime: 60_000,
  })

  useLayoutEffect(() => {
    const place = () => {
      const h = ref.current?.offsetHeight ?? 0
      const left = Math.max(8, Math.min(target.x, window.innerWidth - (target.mode === 'card' ? CARD_WIDTH : MENU_WIDTH) - 8))
      const top = Math.max(8, Math.min(target.y, window.innerHeight - h - 8))
      setPos((p) => (p.left === left && p.top === top ? p : { left, top }))
    }
    place()
    // меню растёт и после открытия: «Роли» появляются, когда участник подгрузится с сервера, —
    // нижние пункты не должны уехать за край окна
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(place)
    ro.observe(el)
    return () => ro.disconnect()
  }, [target.x, target.y, target.mode])

  useEffect(() => {
    if (dialog || nickOpen) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    // прокрутка длинного списка ролей в подменю — не повод закрывать меню
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', close)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', close)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [close, dialog, nickOpen])

  const perms = guild && self ? basePermissions(guild, self) : 0n
  const canModerate = Boolean(guild && target.guildId && !isMe && guild.owner_id !== target.userId)
  const inVoice = Boolean(voice?.channel_id)
  const canKick = canModerate && has(perms, P.KICK_MEMBERS)
  const canBan = canModerate && has(perms, P.BAN_MEMBERS)
  const canTimeout = canModerate && has(perms, MODERATE_MEMBERS)
  // серверный мут и звук — по голосовому состоянию: оно точнее участника
  const serverMuted = voice?.mute ?? member?.mute ?? false
  const serverDeaf = voice?.deaf ?? member?.deaf ?? false
  /*
   * Себе — только снять: если другой модератор заглушил админа, тот должен суметь вернуть себе голос
   * (сервер это разрешает, а меню себя пряталось). Заглушить самого себя незачем: для этого микрофон.
   */
  const canMute = inVoice && has(perms, P.MUTE_MEMBERS) && (canModerate || (isMe && serverMuted))
  const canDeafen = inVoice && has(perms, P.DEAFEN_MEMBERS) && (canModerate || (isMe && serverDeaf))
  // переносить и отключать — с правом в том канале, где человек сейчас: так проверяет и сервер
  const voiceChannel = voice?.channel_id ? channels?.[voice.channel_id] : undefined
  const canMove = canModerate && Boolean(guild && self && voiceChannel && has(channelPermissions(guild, self, voiceChannel), P.MOVE_MEMBERS))
  // куда перенести: голосовые каналы, куда можно самому; музыкальный бот за переносом не следит
  const moveTargets = canMove && guild && self && !user.bot
    ? voiceChannelsInOrder(Object.values(channels ?? {})).filter((ch) => ch.id !== voice?.channel_id && has(channelPermissions(guild, self, ch), P.CONNECT))
    : []

  // роли: себе и тем, кто ниже по иерархии, — как в настройках сервера. Участника может не быть
  // в сторе (автор старого сообщения на большом сервере) — тогда берём его с сервера
  const canManageRoles = Boolean(guild && self && has(perms, P.MANAGE_ROLES))
  const fetched = useQuery({
    queryKey: ['member', target.guildId, target.userId],
    queryFn: () => guildApi.member(target.guildId!, target.userId),
    enabled: canManageRoles && !member && target.mode === 'menu',
    staleTime: 30_000,
    // не участник (вебхук, вышедший автор) — 404, переспрашивать незачем
    retry: false,
  })
  const roleMember = member ?? fetched.data
  const guildRoles = guild ? guild.roles.filter((r) => r.id !== guild.id).sort((a, b) => b.position - a.position) : []
  const myTop = guild && self ? (guild.owner_id === self.user.id ? Infinity : (topRole(guild.roles, self.roles)?.position ?? 0)) : 0
  const outranks = Boolean(guild && roleMember && (isMe || (guild.owner_id !== target.userId && (topRole(guild.roles, roleMember.roles)?.position ?? 0) < myTop)))
  const canRoles = canManageRoles && guildRoles.length > 0 && outranks
  // свой ник — с CHANGE_NICKNAME, чужой — с MANAGE_NICKNAMES; владельцу ник меняет только он сам
  const canNick = Boolean(guild && target.guildId) && (isMe ? has(perms, P.CHANGE_NICKNAME) || has(perms, P.MANAGE_NICKNAMES) : canModerate && has(perms, P.MANAGE_NICKNAMES))
  const timedOut = Boolean(member?.communication_disabled_until && new Date(member.communication_disabled_until) > new Date())
  const isFriend = relationship?.type === 1
  const isBlocked = relationship?.type === 2
  const requested = relationship?.type === 3 || relationship?.type === 4

  const run = (fn: () => Promise<unknown>, andClose = true) => {
    setBusy(true)
    setError(null)
    void fn()
      .then(() => {
        if (andClose) close()
      })
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }

  const openDM = () => run(async () => {
    const ch = await socialApi.openDM(target.userId)
    setView({ kind: 'home', channelId: ch.id })
  })
  const addFriend = () => run(() => socialApi.requestFriend(user.username), false)
  const dmUnread = Boolean(dmChannel && (isUnread(dmChannel, dmReadState) || (dmReadState?.mention_count ?? 0) > 0))
  const markDMRead = () => run(() => (dmChannel ? ack(dmChannel.id) : Promise.resolve()))
  const toggleDMMute = () => run(() => updateSettings(DM_SETTINGS, { channel_overrides: { [target.dmChannelId!]: { muted: !dmMuted } } }))
  const unblock = () => run(() => socialApi.removeRelationship(target.userId), false)
  const copyId = () => run(() => navigator.clipboard.writeText(target.userId))
  const patchMember = (patch: Parameters<typeof guildApi.updateMember>[2]) =>
    run(() => guildApi.updateMember(target.guildId!, target.userId, patch))
  const timeout = (minutes: number | null) =>
    patchMember({ communication_disabled_until: minutes ? new Date(Date.now() + minutes * 60_000).toISOString() : null })
  // галочка роли: меню остаётся открытым, чтобы выдать несколько подряд
  const toggleRole = (roleId: string, on: boolean) => run(() => setMemberRole(target.guildId!, target.userId, roleId, on, roleMember), false)

  const cardProps = {
    user,
    member,
    guild,
    guilds,
    profile: profile.data,
    status,
    activity,
    customStatus,
    activityLine: activityOf(activities)?.label,
    isMe,
    canAddFriend: !isFriend && !isBlocked && !requested && known,
    busy,
    error,
    onOpenDM: openDM,
    onAddFriend: addFriend,
  }

  if (dialog === 'profile') {
    return (
      <ProfileModal
        {...cardProps}
        tab={profileTab}
        onClose={close}
      />
    )
  }

  if (target.mode === 'card') {
    return (
      <div
        ref={ref}
        className={cx(styles.card, 'klichat-popover')}
        style={{ left: pos.left, top: pos.top, '--popover-origin': 'top left' } as CSSProperties}
        onContextMenu={(e) => e.preventDefault()}
      >
        <ProfileCard
          {...cardProps}
          showSend
          onOpenMutuals={(t) => { setProfileTab(t); setDialog('profile') }}
          onSend={(text) => run(async () => {
            const ch = await socialApi.openDM(target.userId)
            await sendMessage(ch.id, text)
            setView({ kind: 'home', channelId: ch.id })
          })}
        />
      </div>
    )
  }
  if (dialog === 'report') {
    return <ReportModal target={{ type: 'user', id: target.userId, label: `пользователя ${known ? '@' + user.username : target.userId}` }} onClose={close} />
  }
  if (nickOpen && target.guildId) return <NickModal guildId={target.guildId} user={user} nick={member?.nick ?? null} onClose={close} />
  if (dialog === 'kick' || dialog === 'ban') {
    const ban = dialog === 'ban'
    return (
      <ConfirmModal
        title={ban ? `Забанить ${name}?` : `Выгнать ${name}?`}
        text={ban ? 'Пользователь не сможет вернуться на сервер, пока бан не снимут в настройках сервера.' : 'Пользователь сможет вернуться по новому приглашению.'}
        confirmLabel={ban ? 'Забанить' : 'Выгнать'}
        onConfirm={async () => {
          if (ban) await guildApi.ban(target.guildId!, target.userId)
          else await guildApi.kick(target.guildId!, target.userId)
          close()
        }}
        onCancel={close}
      />
    )
  }

  return (
    <div ref={ref} className={cx(styles.menu, 'klichat-popover')} role="menu" style={{ left: pos.left, top: pos.top, width: MENU_WIDTH, '--popover-origin': 'top left' } as CSSProperties} onContextMenu={(e) => e.preventDefault()}>
      <div className={styles.header}>
        <span className={styles.name}>{name}</span>
        {known ? <span className={styles.username}>@{user.username}</span> : null}
      </div>
      {inRoom && !isMe ? (
        <div className={styles.volume}>
          <div className={styles.volumeHead}>
            <span><Volume2 size={14} /> Громкость</span>
            <span>{Math.round(volume * 100)}%</span>
          </div>
          <input type="range" min={0} max={200} step={1} value={Math.round(volume * 100)} aria-label="Громкость собеседника" onChange={(e) => setUserVolume(target.userId, Number(e.target.value) / 100)} />
        </div>
      ) : null}
      {inRoom && !isMe && streaming ? (
        // звук трансляции — отдельной ручкой: в него попадает весь звук чужого компьютера,
        // включая наши же голоса, и слышно самого себя
        <div className={styles.volume}>
          <div className={styles.volumeHead}>
            <span><MonitorSpeaker size={14} /> Звук трансляции</span>
            <span>{Math.round(streamVolume * 100)}%</span>
          </div>
          <input type="range" min={0} max={200} step={1} value={Math.round(streamVolume * 100)} aria-label="Громкость звука трансляции" onChange={(e) => setStreamVolume(target.userId, Number(e.target.value) / 100)} />
        </div>
      ) : null}
      {dmChannel ? (
        <>
          <button type="button" role="menuitem" className={styles.item} disabled={!dmUnread || busy} onClick={markDMRead}><Check size={16} /> Отметить как прочитанное</button>
          <div className={styles.divider} />
        </>
      ) : null}
      <button type="button" role="menuitem" className={styles.item} onClick={() => setDialog('profile')}><UserRound size={16} /> Профиль</button>
      {canNick ? <button type="button" role="menuitem" className={styles.item} onClick={() => setNickOpen(true)}><PencilLine size={16} /> Изменить ник</button> : null}
      {!isMe ? (
        <>
          <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={openDM}><MessageSquare size={16} /> Написать</button>
          {isBlocked ? (
            <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={unblock}><UserPlus size={16} /> Разблокировать</button>
          ) : isFriend ? (
            <button type="button" role="menuitem" className={styles.item} disabled><UserPlus size={16} /> В друзьях</button>
          ) : requested ? (
            <button type="button" role="menuitem" className={styles.item} disabled><UserPlus size={16} /> Запрос отправлен</button>
          ) : known ? (
            <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={addFriend}><UserPlus size={16} /> Добавить в друзья</button>
          ) : null}
        </>
      ) : null}
      {dmChannel ? (
        <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={toggleDMMute}>
          {dmMuted ? <Bell size={16} /> : <BellOff size={16} />} {dmMuted ? 'Включить уведомления' : 'Заглушить беседу'}
        </button>
      ) : null}
      {devMode ? <button type="button" role="menuitem" className={styles.item} onClick={copyId}><Copy size={16} /> Скопировать ID</button> : null}
      {!isMe ? <button type="button" role="menuitem" className={styles.item} onClick={() => setDialog('report')}><Flag size={16} /> Пожаловаться</button> : null}
      {canRoles && guild && self && roleMember ? (
        <>
          <div className={styles.divider} />
          <SubMenu icon={<Shield size={16} />} label="Роли" open={sub === 'roles'} onOpen={() => setSub('roles')} onClose={() => setSub(null)}>
            {guildRoles.map((r) => {
              const hint = assignLockHint(guild, self, r)
              const on = roleMember.roles.includes(r.id)
              return (
                <button
                  key={r.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={on}
                  aria-disabled={hint || busy ? true : undefined}
                  data-tooltip={hint}
                  className={cx(styles.item, hint && styles.locked)}
                  onClick={() => {
                    if (!hint && !busy) toggleRole(r.id, !on)
                  }}
                >
                  <span className={styles.roleDot} style={{ background: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : 'var(--text-muted)' }} />
                  <span className={styles.label}>{r.name}</span>
                  {hint ? <Lock size={12} className={styles.lock} aria-label={hint} /> : null}
                  <span className={cx(styles.box, on && styles.boxOn)} aria-hidden="true">{on ? <Check size={12} strokeWidth={3} /> : null}</span>
                </button>
              )
            })}
          </SubMenu>
        </>
      ) : null}
      {canTimeout || canMute || canDeafen || canMove || canKick || canBan ? (
        <>
          <div className={styles.divider} />
          <div className={styles.group}>Модерация</div>
          {canTimeout ? (timedOut ? (
            <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={() => timeout(null)}><Timer size={16} /> Снять тайм-аут</button>
          ) : (
            <>
              <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={() => timeout(60)}><Timer size={16} /> Тайм-аут на час</button>
              <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={() => timeout(24 * 60)}><Timer size={16} /> Тайм-аут на сутки</button>
            </>
          )) : null}
          {canMute ? (
            <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={() => patchMember({ mute: !serverMuted })}>
              {serverMuted ? <Mic size={16} /> : <MicOff size={16} />} {serverMuted ? 'Снять серверный мут' : 'Заглушить на сервере'}
            </button>
          ) : null}
          {canDeafen ? (
            <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={() => patchMember({ deaf: !serverDeaf })}>
              {serverDeaf ? <Headphones size={16} /> : <HeadphoneOff size={16} />} {serverDeaf ? 'Включить звук' : 'Выключить звук'}
            </button>
          ) : null}
          {moveTargets.length > 0 ? (
            <SubMenu icon={<ArrowRightLeft size={16} />} label="Переместить в…" open={sub === 'move'} onOpen={() => setSub('move')} onClose={() => setSub(null)}>
              {moveTargets.map((ch) => (
                <button key={ch.id} type="button" role="menuitem" className={styles.item} disabled={busy} onClick={() => patchMember({ channel_id: ch.id })}>
                  <Volume2 size={16} /> <span className={styles.label}>{ch.name}</span>
                </button>
              ))}
            </SubMenu>
          ) : null}
          {canMove ? (
            <button type="button" role="menuitem" className={styles.item} disabled={busy} onClick={() => patchMember({ channel_id: null })}><PhoneOff size={16} /> Отключить от голоса</button>
          ) : null}
          {canKick ? <button type="button" role="menuitem" className={cx(styles.item, styles.danger)} onClick={() => setDialog('kick')}><LogOut size={16} /> Выгнать с сервера</button> : null}
          {canBan ? <button type="button" role="menuitem" className={cx(styles.item, styles.danger)} onClick={() => setDialog('ban')}><Ban size={16} /> Забанить</button> : null}
        </>
      ) : null}
      {error ? <div className={styles.error} role="alert">{error}</div> : null}
    </div>
  )
}

/**
 * Пункт с подменю, как в Discord: открывается наведением или щелчком и встаёт сбоку от меню —
 * справа, у правого края окна слева, а если не влезает ни там, ни там, то поверх. Подменю —
 * ребёнок строки: пока мышь идёт к нему по прозрачному мостику, она не покидает строку,
 * и подменю не закрывается.
 */
function SubMenu({ icon, label, open, onOpen, onClose, children }: { icon: ReactNode; label: string; open: boolean; onOpen: () => void; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)

  // размер подменю известен только после отрисовки; ставим в стиль до показа кадра, как ContextMenu
  useLayoutEffect(() => {
    const row = ref.current?.getBoundingClientRect()
    const el = panel.current
    if (!open || !row || !el) return
    const w = el.offsetWidth
    const left = row.right + w <= window.innerWidth ? row.width : row.left - w >= 0 ? -w : Math.max(0, window.innerWidth - w) - row.left
    // первая строка подменю — вровень с пунктом: минус отступ и рамка панели
    const top = Math.max(8, Math.min(row.top - 7, window.innerHeight - el.offsetHeight - 8)) - row.top
    el.style.left = `${left}px`
    el.style.top = `${top}px`
  })

  return (
    <div ref={ref} className={styles.sub} onMouseEnter={onOpen} onMouseLeave={onClose}>
      <button
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        className={cx(styles.item, open && styles.itemOpen)}
        // щелчок только открывает: на телефоне перед ним приходит ещё и наведение,
        // и переключатель тут же закрыл бы только что открытое
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') onOpen()
          if (e.key === 'ArrowLeft') onClose()
        }}
      >
        {icon} {label} <ChevronRight size={16} className={styles.subArrow} />
      </button>
      {open ? (
        <div ref={panel} className={styles.flyout}>
          <div className={styles.flyoutPanel} role="menu" aria-label={label}>
            {children}
          </div>
        </div>
      ) : null}
    </div>
  )
}
